import type {
  PendencyResolutionOutcome,
  PendencyWireItem,
  PendencyWireStatus,
} from '../../lib/api-client';

/**
 * Adapter puro da Tela 3 (Pendências) — agrupamento por prazo, contadores
 * derivados, filtros client-side e mapas de rótulo. Sem React, 100% testável.
 *
 * Decisões de contrato (vide spec 2026-06-06): agrupamento/filtros de
 * prioridade/categoria/prazo são CLIENT-SIDE (D2); contadores são derivados do
 * carregado (D3); rótulos degradam honesto p/ valor desconhecido (D6 + §4).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Estados "ativos" — entram nos buckets de prazo. */
const ACTIVE_STATUSES: ReadonlySet<PendencyWireStatus> = new Set([
  'open',
  'in_progress',
  'postponed',
]);

function isActive(item: PendencyWireItem): boolean {
  return ACTIVE_STATUSES.has(item.status);
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Item ativo vence "hoje" (deadline dentro do dia de `nowMs`) e não está vencido. */
function isDueToday(item: PendencyWireItem, nowMs: number): boolean {
  if (item.overdue || item.deadline === null) return false;
  const dl = new Date(item.deadline).getTime();
  const dayStart = startOfDay(nowMs);
  return dl >= dayStart && dl < dayStart + DAY_MS;
}

export interface DeadlineGroups {
  overdue: PendencyWireItem[];
  dueToday: PendencyWireItem[];
  next7: PendencyWireItem[];
  /** Ativas sem `deadline` (Noturna 2 N2-11) — antes caíam em `next7`. */
  noDeadline: PendencyWireItem[];
  resolvedRecent: PendencyWireItem[];
}

/**
 * Agrupa por prazo (D2 client-side), espelhando as seções do mockup:
 * Vencidas · Vence hoje · Próximos 7 dias · Sem prazo · Recentemente resolvidas.
 *
 * Itens ativos com prazo futuro caem em `next7`; ativos SEM prazo em
 * `noDeadline` (N2-11: a seção «Próximos 7 dias» agrupava «Sem prazo» — a
 * página dizia uma coisa e o card outra). Nada é silenciosamente descartado.
 * Itens terminais (resolved/cancelled/expired/archived) vão p/ `resolvedRecent`.
 *
 * Imprecisão DECLARADA: prazo além de 7 dias também fica em `next7` — fora
 * do escopo N2-11.
 */
export function groupByDeadline(items: PendencyWireItem[], nowMs: number): DeadlineGroups {
  const groups: DeadlineGroups = {
    overdue: [],
    dueToday: [],
    next7: [],
    noDeadline: [],
    resolvedRecent: [],
  };
  for (const item of items) {
    if (!isActive(item)) {
      groups.resolvedRecent.push(item);
    } else if (item.overdue) {
      groups.overdue.push(item);
    } else if (isDueToday(item, nowMs)) {
      groups.dueToday.push(item);
    } else if (item.deadline === null) {
      groups.noDeadline.push(item);
    } else {
      groups.next7.push(item);
    }
  }
  return groups;
}

export interface PendencyCounters {
  open: number;
  overdue: number;
  dueToday: number;
  postponed: number;
  postponeRatePct: number;
}

/** Contadores honestos derivados do conjunto carregado (D3). */
export function deriveCounters(items: PendencyWireItem[], nowMs: number): PendencyCounters {
  let open = 0;
  let overdue = 0;
  let dueToday = 0;
  let postponed = 0;
  let postponedEver = 0;
  for (const item of items) {
    if (isActive(item)) open += 1;
    if (item.overdue && isActive(item)) overdue += 1;
    if (isDueToday(item, nowMs)) dueToday += 1;
    if (item.status === 'postponed') postponed += 1;
    if (item.postponeCount > 0) postponedEver += 1;
  }
  const postponeRatePct = items.length > 0 ? Math.round((postponedEver / items.length) * 100) : 0;
  return { open, overdue, dueToday, postponed, postponeRatePct };
}

export type DueWindow = 'overdue' | 'today' | 'next7' | 'none';

export interface ClientFilters {
  priorities?: PendencyWireItem['priority'][];
  categories?: string[];
  dueWindow?: DueWindow | null;
}

function matchesDueWindow(item: PendencyWireItem, dueWindow: DueWindow, nowMs: number): boolean {
  switch (dueWindow) {
    case 'overdue':
      return item.overdue && isActive(item);
    case 'today':
      return isDueToday(item, nowMs);
    case 'next7': {
      if (!isActive(item) || item.overdue || item.deadline === null) return false;
      const dl = new Date(item.deadline).getTime();
      return dl > nowMs && dl <= nowMs + 7 * DAY_MS;
    }
    case 'none':
      return item.deadline === null;
  }
}

/** Filtros client-side (D2): prioridade, categoria, janela de prazo. */
export function applyClientFilters(
  items: PendencyWireItem[],
  filters: ClientFilters,
  nowMs: number,
): PendencyWireItem[] {
  const { priorities, categories, dueWindow } = filters;
  return items.filter((item) => {
    if (priorities !== undefined && priorities.length > 0 && !priorities.includes(item.priority)) {
      return false;
    }
    if (
      categories !== undefined &&
      categories.length > 0 &&
      !categories.includes(item.category ?? '')
    ) {
      return false;
    }
    if (
      dueWindow !== undefined &&
      dueWindow !== null &&
      !matchesDueWindow(item, dueWindow, nowMs)
    ) {
      return false;
    }
    return true;
  });
}

// ─── Mapas de rótulo — conhecidos + degradação honesta (§4 + D6) ───

const STATUS_LABELS: Record<PendencyWireStatus, string> = {
  open: 'Aberta',
  in_progress: 'Em curso',
  postponed: 'Adiada',
  resolved: 'Resolvida',
  cancelled: 'Cancelada',
  expired: 'Expirada',
  archived: 'Arquivada',
};

export function statusLabel(status: PendencyWireStatus, postponeCount = 0): string {
  if (status === 'postponed' && postponeCount > 0) return `Adiada ${postponeCount}×`;
  return STATUS_LABELS[status] ?? String(status);
}

/** 7 categorias canônicas (ADR-109). `tone` mapeia a variante visual do ícone. */
const CATEGORY_LABELS: Record<string, string> = {
  customer_contact_required: 'Contato cliente',
  document_collection: 'Coleta documento',
  external_action_required: 'Ação externa',
  internal_review: 'Revisão interna',
  technical_escalation: 'Escalation técnico',
  commitment_fulfillment_confirmation: 'Compromisso',
  ai_suggestion_review: 'Sugestão IA',
};

export function categoryMeta(category: string | null): { label: string; key: string | null } {
  if (category === null) return { label: 'Sem categoria', key: null };
  return { label: CATEGORY_LABELS[category] ?? category, key: category };
}

/** Lista ordenada das categorias conhecidas — alimenta o filter-rail (client-side). */
export const KNOWN_CATEGORIES: { key: string; label: string }[] = Object.entries(
  CATEGORY_LABELS,
).map(([key, label]) => ({ key, label }));

/** Opções de prioridade (filtro client-side). */
export const PRIORITY_OPTIONS: { key: PendencyWireItem['priority']; label: string }[] = [
  { key: 'urgent', label: 'Urgente' },
  { key: 'high', label: 'Alta' },
  { key: 'medium', label: 'Média' },
  { key: 'low', label: 'Baixa' },
];

/** 7 origens canônicas (ADR-109 — `source` free-form). */
const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manual',
  'classification-agent': 'IA classificou',
  'suggestion-agent': 'IA sugeriu',
  customer_commitment_overdue: 'Compromisso vencido',
  conversation_paused: 'Conversa pausada',
  appointment_no_show: 'Não compareceu',
  external_system: 'Sistema externo',
  // Frente B Sessão 4 — Pendency criada pela promoção manual da fila (waitlist.service
  // `promoteManual`, source='waitlist_promotion'): uma vaga abriu e o operador escolhe
  // quem da fila liberar. Render dedicado no drawer + deep-link p/ a /agenda.
  waitlist_promotion: 'Fila de espera',
  // ADR-155 Fase 4 (§3.7) — Pendency de revisão gerada pelo finalize da
  // Coleta Roteada por Durabilidade (`structured-collections.service.ts`).
  structured_collection: 'Coleta de dados',
  // §D4 (26/Set/2026) — tarefa que a IA cria para a equipe a partir de pendências de validação
  // (`pending-validation-writer.service.ts`, source='ai-pending-validation').
  'ai-pending-validation': 'IA pediu confirmação',
};

export function originMeta(source: string): { label: string } {
  return { label: ORIGIN_LABELS[source] ?? source };
}

/**
 * Frente B Sessão 4 — é a Pendency de liberação manual da fila de espera? Identifica
 * pela `source` canônica do `promoteManual` do backend. O render dedicado (drawer)
 * conduz o operador à /agenda, onde a liberação (WaitlistPanel + promoção manual) já mora.
 */
export function isWaitlistReleasePendency(source: string): boolean {
  return source === 'waitlist_promotion';
}

/**
 * ADR-155 Fase 4 (§3.7) — é a Pendency de revisão gerada pelo `finalize` da
 * Coleta Roteada por Durabilidade? Identifica pela `source` canônica
 * (`structured-collections.service.ts` — `source: 'structured_collection'`).
 * O render dedicado (drawer) mostra a description (que já traz o roteamento
 * por-campo e, com E5, o conflito dupla-versão B2B) + deep-link para reabrir
 * a coleta na conversa de origem.
 */
export function isStructuredCollectionPendency(source: string): boolean {
  return source === 'structured_collection';
}

const OUTCOME_LABELS: Record<PendencyResolutionOutcome, string> = {
  success: 'Concluída com sucesso',
  with_restrictions: 'Concluída com restrições',
  not_possible: 'Não foi possível concluir',
  customer_gave_up: 'Cliente desistiu',
  replaced: 'Substituída por nova pendência',
  other: 'Outro',
};

export function outcomeLabel(outcome: PendencyResolutionOutcome | null): string {
  if (outcome === null) return '';
  return OUTCOME_LABELS[outcome] ?? String(outcome);
}

/** Data/hora curta pt-BR («08 de set. de 2026, 12:00») — o formatador da feature; não duplicar. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Base sobre a qual um preset de adiamento («+ 1 dia») soma. Adiar é EMPURRAR
 * o prazo: com prazo atual no futuro, a base é ele; vencido ou inexistente, a
 * base é agora. Nunca antecipa — N2-12 (noturna 2, 05/Set/2026): com prazo em
 * 07/09 12:00, «+ 1 dia» clicado em 05/09 06:54 gravava 06/09 06:54 (−29 h).
 */
export function postponeBaseMs(deadline: string | null, nowMs: number): number {
  if (deadline === null) return nowMs;
  const deadlineMs = new Date(deadline).getTime();
  return Number.isFinite(deadlineMs) ? Math.max(nowMs, deadlineMs) : nowMs;
}
